/**
 * PhaseStatusBadge — pill que muestra el status de una fase del lifecycle
 * con tokens semánticos de marca (Doc 18 §2.4).
 *
 * Mapeo:
 *   approved     → official (verde)
 *   in_progress  → review (naranja)
 *   blocked      → critical (rojo)
 *   not_started  → neutral (surface-muted)
 *   skipped      → neutral con texto subtle
 *
 * Cuando la fase es `external_canon` (literal del API; UI label
 * "client-provided") y no hay status interno (o está `not_started`),
 * preferimos un badge especial "Aportado por el cliente" en vez
 * de "Pending" — el lifecycle UI se lo pasa via `contractMode`.
 */

import { useTranslation } from 'react-i18next'

import type { ProjectPhaseStatus, ContractMode } from '../../api/projects'

interface PhaseStatusBadgeProps {
  status: ProjectPhaseStatus
  /** Cuando es `external_canon` (API literal; UI label client-provided)
   * y la phase no está aprobada todavía, el badge prioriza este modo
   * en lugar del status. */
  contractMode?: ContractMode | undefined
  /** Tamaño compacto para el timeline (default) o normal para el header
   * del drill-in. */
  size?: 'xs' | 'sm'
}

export function PhaseStatusBadge({
  status,
  contractMode,
  size = 'xs',
}: PhaseStatusBadgeProps) {
  const { t } = useTranslation('projects')

  // Lógica de override: client-provided visible mientras NO esté aprobada
  // (cuando se aprueba, mostramos "Approved" — significa que el cliente
  // ya entregó y tomamos la entrega como oficial).
  // 'external_canon' is the API enum literal; surfaced as client_provided.
  const useClientProvidedLabel =
    contractMode === 'external_canon' && status !== 'approved'
  const useSkippedLabel = contractMode === 'skipped' || status === 'skipped'

  const labelKey = useSkippedLabel
    ? 'lifecycle.legend.skipped'
    : useClientProvidedLabel
      ? 'lifecycle.legend.client_provided'
      : `lifecycle.legend.${status}`

  const className = useSkippedLabel
    ? CLASS_NEUTRAL_SUBTLE
    : useClientProvidedLabel
      ? CLASS_REVIEW
      : CLASS_BY_STATUS[status]

  const sizeClass =
    size === 'sm'
      ? 'px-2 py-0.5 text-[10px]'
      : 'px-2 py-0.5 text-[9px]'

  return (
    <span
      className={`${sizeClass} rounded-full font-medium uppercase tracking-wider ${className}`}
    >
      {t(labelKey as 'lifecycle.legend.approved')}
    </span>
  )
}

const CLASS_BY_STATUS: Record<ProjectPhaseStatus, string> = {
  approved: 'bg-official-soft text-official',
  in_progress: 'bg-review-soft text-review',
  blocked: 'bg-critical-soft text-critical',
  not_started: 'bg-surface-muted text-ink-muted',
  skipped: 'bg-surface-muted text-ink-subtle',
}
const CLASS_NEUTRAL_SUBTLE = 'bg-surface-muted text-ink-subtle'
const CLASS_REVIEW = 'bg-surface-muted text-ink-subtle border border-line'
